import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BodyText, Div, Icon, Link, ModalDialog, Table } from '@gaiads/telia-react-component-library';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { addOneLocale, removeOneLocale, selectAllLocales } from '../../../store/locales';
import { setNotification } from '../../../store/ui';
import { getInitialLocale } from '../../../utils/categories';
import { defaultLocale, availableLocales, Language } from '../../../utils/locales';
import LocaleRow from './LocaleRow/LocaleRow';
import { productServiceLocalesClient } from '../../../api-client/product-service-client';

export default function LocaleSettings({ setIsReady }: { setIsReady: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { t } = useTranslation('adminSettings');

  const dispatch = useDispatch();

  const locales = useSelector(selectAllLocales);
  const [languages, setLanguages] = useState<Language[]>();
  const [newLocale, setNewLocale] = useState<LocaleResponse>();
  const [localeIdToDelete, setLocaleIdToDelete] = useState<LocaleResponse['id']>();

  useEffect(() => {
    setLanguages(availableLocales);
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocaleSave = async (locale: LocaleResponse): Promise<boolean> => {
    try {
      await productServiceLocalesClient.deleteLocale(locale.id);
      const response = await productServiceLocalesClient.postLocale(locale);
      dispatch(removeOneLocale(locale.id));
      dispatch(addOneLocale(response.data));
      return true;
    } catch {
      dispatch(
        setNotification({
          text: t('localeSettings.errors.updateLocale'),
          show: true,
          type: 'warning',
        }),
      );
      return false;
    }
  };

  const handleNewLocaleSave = async (locale: LocaleResponse): Promise<boolean> => {
    try {
      const response = await productServiceLocalesClient.postLocale(locale);
      dispatch(addOneLocale(response.data));
      setNewLocale(undefined);
    } catch {
      dispatch(
        setNotification({
          text: t('localeSettings.errors.createLocale'),
          show: true,
          type: 'warning',
        }),
      );
    }
    return false;
  };

  const handleNewLocaleAddition = () => {
    const locale = getInitialLocale();
    setNewLocale(locale);
  };

  const handleDelete = (id: LocaleResponse['id']) => {
    setLocaleIdToDelete(id);
  };

  const handleLocaleDelete = async (id: LocaleResponse['id']) => {
    try {
      await productServiceLocalesClient.deleteLocale(id);
      dispatch(removeOneLocale(id));
      setLocaleIdToDelete(undefined);
    } catch {
      dispatch(
        setNotification({
          text: t('localeSettings.errors.deleteLocale'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  const handleNewLocaleBlur = () => {
    setNewLocale(undefined);
  };

  if (!languages) {
    return <></>;
  } else {
    return (
      <>
        <BodyText margin={{ bottom: 'md' }}>{t('localeSettings.description')}</BodyText>
        <BodyText margin={{ bottom: 'md' }}>{t('localeSettings.note')}</BodyText>
        <Table>
          <thead>
            <Table.Row>
              <Table.HeaderCell>
                <BodyText>{t('localeSettings.tableTitles.name')}</BodyText>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <BodyText>{t('localeSettings.tableTitles.nameInNativeLanguage')}</BodyText>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <BodyText>{t('localeSettings.tableTitles.languageTag')}</BodyText>
              </Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </thead>
          <tbody>
            <LocaleRow locale={defaultLocale} languages={languages} isDefault />
            {locales.map(locale => (
              <LocaleRow key={`locale-${locale.id}`} locale={locale} languages={languages} onLocaleSave={handleLocaleSave} onDelete={handleDelete} />
            ))}
            {newLocale && (
              <LocaleRow
                key={`locale-${newLocale.id}`}
                locale={newLocale}
                languages={languages}
                onLocaleSave={handleNewLocaleSave}
                onBlur={handleNewLocaleBlur}
                onDelete={handleNewLocaleBlur}
                isNew
              />
            )}
          </tbody>
        </Table>
        <Div className="locale-settings__add" onClick={handleNewLocaleAddition}>
          <Link>
            <Icon name="plus" size="sm" alignTextToCenter className="locale-settings__add-icon" />
            {t('localeSettings.links.addLocale')}
          </Link>
        </Div>
        <ModalDialog
          title={t('localeSettings.deleteLocaleConfirmationDialog.title')}
          closeButtonText={t('localeSettings.deleteLocaleConfirmationDialog.buttons.cancel')}
          acceptButtonText={t('localeSettings.deleteLocaleConfirmationDialog.buttons.delete')}
          isOpen={!!localeIdToDelete}
          onCloseClick={() => setLocaleIdToDelete(undefined)}
          onAcceptClick={() => localeIdToDelete && handleLocaleDelete(localeIdToDelete)}
        >
          <BodyText>{t('localeSettings.deleteLocaleConfirmationDialog.content')}</BodyText>
        </ModalDialog>
      </>
    );
  }
}
